import React from 'react';
import type { AttentionItem } from '../../domain/types';
import { Avatar, Badge, Button, ChevronIcon, SlackRichText } from '../../ui/common';
import type { ComposerMentionCandidate } from '../../ui/common/RichTextComposer';
import { RichTextComposer } from '../../ui/common/RichTextComposer';

interface SlackCardExpandedProps {
  item: AttentionItem;
  summaryOpen: boolean;
  isInMerge: boolean;
  replyDraft: string;
  isSending: boolean;
  isDrafting: boolean;
  sendError: string | null | undefined;
  expandedRootRef: React.Ref<HTMLDivElement>;
  threadEndRef: React.Ref<HTMLDivElement>;
  onScrollToEnd: () => void;
  onToggleMerge: (e: React.MouseEvent) => void;
  onToggleSummary: (e: React.MouseEvent) => void;
  onNavigateToJira: (key: string) => void;
  isJiraRefResolved?: (key: string) => boolean;
  onReplyChange: (value: string) => void;
  onSubmitReply: () => void;
  onAskAboutThread: (e: React.MouseEvent) => void;
  onGenerateDraft: (e: React.MouseEvent) => void;
  mentionCandidates?: ComposerMentionCandidate[];
  onMentionCandidateUnavailable?: (candidate: ComposerMentionCandidate) => void;
  onSend: (e: React.MouseEvent) => void;
}

function SlackRefChips({
  refs,
  onNavigate,
  isResolved,
}: {
  refs: NonNullable<AttentionItem['refs']>;
  onNavigate: (key: string) => void;
  isResolved?: (key: string) => boolean;
}) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
      {refs.map((rf) => {
        const resolved = isResolved ? isResolved(rf.key) : true;
        return (
          <button
            type="button"
            key={rf.key}
            aria-label={resolved ? `Ver ${rf.key}` : `${rf.key}: no está cargado en Zunelo`}
            aria-disabled={!resolved}
            disabled={!resolved}
            title={resolved ? undefined : 'No está cargado en Zunelo'}
            onClick={(e) => {
              if (!resolved) return;
              e.stopPropagation();
              onNavigate(rf.key);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '5px 11px',
              borderRadius: 999,
              cursor: resolved ? 'pointer' : 'not-allowed',
              opacity: resolved ? 1 : 0.55,
              background: 'var(--z-accent-wash)',
              border: 'none',
            }}
          >
            <span
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                fontWeight: 700,
                color: 'var(--z-accent-ink)',
              }}
            >
              {rf.key}
            </span>
            <span style={{ fontSize: 11.5, color: 'var(--z-accent-copy)' }}>{rf.why}</span>
          </button>
        );
      })}
    </div>
  );
}

function SlackThreadToolbar({
  item,
  isInMerge,
  summaryOpen,
  onScrollToEnd,
  onToggleMerge,
  onToggleSummary,
}: {
  item: AttentionItem;
  isInMerge: boolean;
  summaryOpen: boolean;
  onScrollToEnd: () => void;
  onToggleMerge: (e: React.MouseEvent) => void;
  onToggleSummary: (e: React.MouseEvent) => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        padding: '8px 11px',
        borderRadius: 11,
        background: 'var(--z-plate)',
        border: 'none',
        flexWrap: 'wrap',
      }}
    >
      <span
        style={{
          fontSize: 12,
          color: 'var(--z-dim)',
          flex: '1 1 auto',
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {item.threadMeta}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {item.members?.map((u, mIdx) => (
          <Avatar
            key={u.id || mIdx}
            src={u.avatarUrl}
            name={u.realName || u.name}
            initials={u.initials}
            color={u.color}
            size={22}
          />
        ))}
      </div>
      <button
        type="button"
        data-testid="slack-scroll-to-end"
        onClick={(e) => {
          e.stopPropagation();
          onScrollToEnd();
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          height: 24,
          padding: '0 9px',
          borderRadius: 8,
          fontSize: 11,
          fontWeight: 600,
          cursor: 'pointer',
          background: 'var(--z-fill-2)',
          color: 'var(--z-muted-2)',
          border: 'none',
        }}
      >
        Ir al final
      </button>
      <button
        type="button"
        onClick={onToggleMerge}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          height: 24,
          padding: '0 9px',
          borderRadius: 8,
          fontSize: 11,
          fontWeight: 600,
          cursor: 'pointer',
          background: isInMerge ? 'var(--z-accent)' : 'var(--z-fill-2)',
          color: isInMerge ? 'var(--z-on-accent)' : 'var(--z-muted-2)',
          border: 'none',
        }}
      >
        <span>{isInMerge ? 'En comparación' : 'Comparar hilo'}</span>
      </button>
      <button
        type="button"
        data-testid="slack-summary-toggle"
        onClick={onToggleSummary}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          height: 24,
          padding: '0 10px',
          borderRadius: 8,
          fontSize: 11,
          fontWeight: 600,
          cursor: 'pointer',
          background: summaryOpen ? 'var(--z-accent)' : 'var(--z-accent-soft)',
          color: summaryOpen ? 'var(--z-on-accent)' : 'var(--z-accent-ink)',
          border: 'none',
        }}
      >
        {summaryOpen ? 'Ocultar resumen' : 'Resumir'}
      </button>
    </div>
  );
}

function SlackStickySummary({
  item,
  summaryOpen,
  onToggleSummary,
}: {
  item: AttentionItem;
  summaryOpen: boolean;
  onToggleSummary: (e: React.MouseEvent) => void;
}) {
  return (
    <div
      data-testid="slack-sticky-summary"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 2,
        borderRadius: 11,
        background: 'var(--z-accent-wash)',
        border: 'none',
        boxShadow: '0 1px 0 var(--z-sh-4)',
      }}
    >
      <button
        type="button"
        data-testid="slack-sticky-summary-toggle"
        onClick={onToggleSummary}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 12px',
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'var(--z-accent-3)',
          }}
        >
          Resumen del hilo
        </span>
        <span style={{ marginLeft: 'auto', color: 'var(--z-accent-3)' }}>
          <ChevronIcon size={14} direction={summaryOpen ? 'down' : 'right'} />
        </span>
      </button>
      {summaryOpen ? (
        <div style={{ padding: '0 12px 11px' }}>
          <p
            style={{
              margin: 0,
              fontSize: 12.5,
              lineHeight: 1.55,
              color: 'var(--z-accent-copy)',
              textWrap: 'pretty',
            }}
          >
            <SlackRichText text={item.ai} />
          </p>
        </div>
      ) : null}
    </div>
  );
}

function SlackMessageList({ item }: { item: AttentionItem }) {
  const names = Object.fromEntries((item.members ?? []).map((m) => [m.id, m.realName || m.name]));
  return (
    <>
      {item.messages?.map((msg, idx) => (
        <SlackMessageRow key={msg.ts || idx} msg={msg} names={names} />
      ))}
    </>
  );
}

function SlackMessageRow({
  msg,
  names,
}: {
  msg: NonNullable<AttentionItem['messages']>[number];
  names: Record<string, string>;
}) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 11,
        padding: '12px 13px',
        borderRadius: 13,
        background: msg.tag ? 'var(--z-warn-bg)' : 'var(--z-fill)',
        border: 'none',
      }}
    >
      <Avatar
        src={msg.user.avatarUrl}
        name={msg.user.realName || msg.user.name}
        initials={msg.user.initials}
        color={msg.user.color}
        size={28}
      />
      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--z-text-2)' }}>
            {msg.user.realName || msg.user.name}
          </span>
          <span style={{ fontSize: 11, color: 'var(--z-faint)' }}>{msg.timeFormatted}</span>
          {msg.tag ? <Badge label={msg.tag} tone="warn" /> : null}
        </div>
        <SlackRichText
          as="div"
          text={msg.text}
          names={names}
          style={{
            margin: 0,
            fontSize: 12.5,
            lineHeight: 1.55,
            color: 'var(--z-text-3)',
            textWrap: 'pretty',
          }}
        />
        {msg.codeSnippet ? (
          <pre
            style={{
              margin: '7px 0 0',
              padding: '8px 10px',
              borderRadius: 8,
              background: 'var(--z-fill-2)',
              border: 'none',
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 11,
              color: 'var(--z-muted)',
              overflowX: 'auto',
            }}
          >
            {msg.codeSnippet}
          </pre>
        ) : null}
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 6 }}>
          {msg.reactions?.map((rx, rIdx) => (
            <span
              key={rIdx}
              style={{
                padding: '2px 8px',
                borderRadius: 999,
                background: 'var(--z-fill-2)',
                border: 'none',
                fontSize: 10.5,
                color: 'var(--z-muted-2)',
              }}
            >
              {rx.name} {rx.count}
            </span>
          ))}
          {msg.repliesCount ? (
            <span style={{ fontSize: 11, color: 'var(--z-accent-3)', fontWeight: 600 }}>
              {msg.repliesCount} respuestas · última {msg.lastReplyTime || 'reciente'}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function SlackReplyComposer({
  item,
  replyDraft,
  isSending,
  isDrafting,
  sendError,
  onReplyChange,
  onSubmitReply,
  onAskAboutThread,
  onGenerateDraft,
  onSend,
  mentionCandidates = [],
  onMentionCandidateUnavailable,
}: {
  item: AttentionItem;
  replyDraft: string;
  isSending: boolean;
  isDrafting: boolean;
  sendError: string | null | undefined;
  onReplyChange: (value: string) => void;
  onSubmitReply: () => void;
  onAskAboutThread: (e: React.MouseEvent) => void;
  onGenerateDraft: (e: React.MouseEvent) => void;
  mentionCandidates?: ComposerMentionCandidate[];
  onMentionCandidateUnavailable?: (candidate: ComposerMentionCandidate) => void;
  onSend: (e: React.MouseEvent) => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '10px 11px',
        borderRadius: 11,
        background: 'var(--z-plate)',
        border: 'none',
      }}
    >
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}
      >
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--z-muted-2)' }}>Responder</span>
        <span style={{ fontSize: 10.5, color: 'var(--z-accent-3)', fontWeight: 600 }}>
          ✦ Asistente Zunelo
        </span>
      </div>
      <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <RichTextComposer
          data-testid="slack-reply-composer"
          value={replyDraft}
          onChange={onReplyChange}
          placeholder="Escribe tu respuesta o una idea para el borrador…"
          minHeight={72}
          showBottomExtras
          mentionCandidates={mentionCandidates}
          onMentionCandidateUnavailable={onMentionCandidateUnavailable}
          toolbarDefaultVisible
          submitOnEnter
          onSubmit={onSubmitReply}
          disabled={isSending || isDrafting}
          ariaLabel="Compositor de respuesta de Slack"
        />
      </div>
      <div
        className="z-card-actions-row"
        style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
      >
        <span style={{ flex: '1 1 auto', fontSize: 11.5, color: 'var(--z-faint)' }}>
          en {item.primary}
        </span>
        <Button variant="ghost" size="sm" data-testid="slack-ask-thread" onClick={onAskAboutThread}>
          Preguntar acerca del hilo
        </Button>
        <Button
          variant="ghost"
          size="sm"
          data-testid="slack-generate-draft"
          onClick={onGenerateDraft}
          disabled={!replyDraft.trim() || isDrafting || isSending}
          title={
            replyDraft.trim()
              ? 'Expandir tu idea con el tono y contexto del hilo'
              : 'Escribe primero tu idea o intención en el compositor'
          }
        >
          {isDrafting ? 'Generando…' : 'Generar borrador'}
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={onSend}
          disabled={!replyDraft.trim() || isSending || isDrafting}
        >
          {isSending ? 'Enviando…' : 'Enviar'}
        </Button>
      </div>
      {sendError ? (
        <p
          role="alert"
          style={{
            margin: 0,
            fontSize: 11.5,
            lineHeight: 1.45,
            color: 'var(--z-bad)',
            textWrap: 'pretty',
          }}
        >
          {sendError}
        </p>
      ) : null}
    </div>
  );
}

export const SlackCardExpanded: React.FC<SlackCardExpandedProps> = (props) => {
  const { item, expandedRootRef, threadEndRef } = props;
  return (
    <div
      ref={expandedRootRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: '12px 13px',
        marginBottom: 12,
        borderRadius: 12,
        background: 'var(--z-fill)',
        border: 'none',
        position: 'relative',
      }}
    >
      {item.refs && item.refs.length > 0 ? (
        <SlackRefChips
          refs={item.refs}
          onNavigate={props.onNavigateToJira}
          isResolved={props.isJiraRefResolved}
        />
      ) : null}
      <SlackThreadToolbar
        item={item}
        isInMerge={props.isInMerge}
        summaryOpen={props.summaryOpen}
        onScrollToEnd={props.onScrollToEnd}
        onToggleMerge={props.onToggleMerge}
        onToggleSummary={props.onToggleSummary}
      />
      <SlackStickySummary
        item={item}
        summaryOpen={props.summaryOpen}
        onToggleSummary={props.onToggleSummary}
      />
      <SlackMessageList item={item} />
      <SlackReplyComposer
        item={item}
        replyDraft={props.replyDraft}
        isSending={props.isSending}
        isDrafting={props.isDrafting}
        sendError={props.sendError}
        onReplyChange={props.onReplyChange}
        onSubmitReply={props.onSubmitReply}
        onAskAboutThread={props.onAskAboutThread}
        onGenerateDraft={props.onGenerateDraft}
        onSend={props.onSend}
        mentionCandidates={props.mentionCandidates}
        onMentionCandidateUnavailable={props.onMentionCandidateUnavailable}
      />
      <div ref={threadEndRef} data-testid="slack-thread-end" />
    </div>
  );
};
